import React, {FC, useEffect, useMemo} from 'react';
import {getLocale} from '@epic-core/common/dist';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {Datapoint, compactNumberFormatter, numberFormatter} from '@epic-visualizations/charts';
import {useComparisonColor, useVisualizationsTheme} from '@epic-visualizations/theme';

import useWidth from './useWidth';
import Legends from './legends/Legends';
import Typography from '@material-ui/core/Typography';
import {getCompactXAxis} from './compact-axis/CustomXAxis';
import {getCompactYAxis} from './compact-axis/CustomYAxis';
import {ChartSkeleton} from '../../../../../common/skeleton';
import {StyledLegendMarker, StyledSpan, StyledTooltipContainer} from './CustomTooltip';

import {getColor} from './utils';
import useInteractiveLegends from './legends/useInteractiveLegends';
import useOrdinalNumberFormatter from '../../hooks/useOrdinalNumberFormatter';

import {KeysMap} from './types';
import {Option} from '../../../../../../types';
import useLocalization from '../../../../../../hooks/useLocalization';

import {StyledBarChart, ChartContainer, TooltipWrapper} from './styles';

const CODES = {
    published: 'epic.affiliate.fortnite_games.lifetime_plays.published.label'
};

export interface ChartProps {
    data: Datapoint[];
    height?: number;
    selectedGames: Option[];
    limit?: number;
    loading: boolean;
    keysMap?: KeysMap;
    emptyDataKeys?: string[];
}

const LifetimePlaysBarChart: FC<ChartProps> = ({
    data: propsData,
    height,
    limit = 10,
    loading,
    selectedGames: selectedGamesProps,
    keysMap = {},
    emptyDataKeys = [],
    ...props
}: ChartProps) => {
    const theme = useTheme();
    const getComparisonColor = useComparisonColor();
    const visualizationsTheme = useVisualizationsTheme();
    const formatOrdinal = useOrdinalNumberFormatter();
    const getMessage = useLocalization();

    const isFmdBreakpoint = !useMediaQuery(theme.breakpoints.up('fmd'));
    const [containerRef, width] = useWidth();

    const ranksMap = useMemo(
        () =>
            propsData.reduce((acc, item, index) => {
                acc[String(item.creativeIslandCode)] = index + 1;

                return acc;
            }, {}),
        [propsData]
    );

    const publishTimeFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(getLocale(), {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            }),
        []
    );

    const selectedGames = useMemo(
        () =>
            [...selectedGamesProps].sort(
                ({value: a}: Option, {value: b}: Option) =>
                    Number(ranksMap[a] || 1000) - Number(ranksMap[b] || 1000)
            ),
        [selectedGamesProps, ranksMap]
    );

    // Used to display legends list
    const dataKeys = useMemo(() => selectedGames.map(({value}) => String(value)), [selectedGames]);

    const {
        hoveredKey,
        hiddenKeys,
        setHiddenKeys,
        onLegendClick,
        onLegendMouseEnter,
        onLegendMouseLeave
    } = useInteractiveLegends();

    useEffect(() => {
        setHiddenKeys(
            Array.from(new Set([...dataKeys.slice(limit, dataKeys.length), ...emptyDataKeys]))
        );
    }, [dataKeys, emptyDataKeys]);

    // Override the default BarChart colors for hover effect
    const dataColors = useMemo(
        () =>
            dataKeys
                .sort((a, b) => Number(ranksMap[a]) - Number(ranksMap[b]))
                .reduce(
                    (acc, key, index) => ({
                        ...acc,
                        [key]: getColor(
                            getComparisonColor(index),
                            hoveredKey && !hiddenKeys.includes(hoveredKey) && hoveredKey !== key
                        )
                    }),
                    {}
                ),
        [getComparisonColor, hoveredKey, dataKeys]
    );

    const data = useMemo(() => {
        const keysToShow = [
            'creativeIslandCode',
            ...dataKeys.filter(key => !hiddenKeys.includes(key))
        ];

        return propsData.filter(({creativeIslandCode}) =>
            keysToShow.includes(String(creativeIslandCode))
        );
    }, [propsData, hiddenKeys, dataKeys]);

    if (loading) {
        return <ChartSkeleton pb={2} pt={4} height={520} width="100%" turnOver />;
    }

    const locale = getLocale();

    return (
        <>
            <ChartContainer ref={containerRef}>
                <StyledBarChart
                    responsive
                    height={height}
                    data={data}
                    stacked
                    locales={locale}
                    showLegend={false}
                    xAxis="creativeIslandCode"
                    dataColors={dataColors}
                    margin={{top: 5, right: 0, bottom: 0, left: 0}}
                    yTickFormatter={compactNumberFormatter(locale)}
                    xTickFormatter={value => formatOrdinal(+ranksMap[value])}
                    tooltipLabelFormatter={value => formatOrdinal(+ranksMap[value])}
                    tooltipContent={({payload}: {payload: any}) => {
                        const [{dataKey, fill, value}] = payload;
                        const {day = 0} = keysMap[dataKey] || {};

                        return (
                            <TooltipWrapper>
                                <StyledTooltipContainer>
                                    <StyledLegendMarker style={{backgroundColor: fill}} />
                                    <StyledSpan>{String(keysMap[dataKey])}:</StyledSpan>
                                    <StyledSpan>{numberFormatter(getLocale())(value)}</StyledSpan>
                                </StyledTooltipContainer>
                                {Boolean(day) && (
                                    <Typography variant="caption" color="textSecondary">
                                        {getMessage(CODES.published, [
                                            publishTimeFormatter.format(
                                                new Date(isNaN(day) ? day : +day)
                                            )
                                        ])}
                                    </Typography>
                                )}
                            </TooltipWrapper>
                        );
                    }}
                    xAxisProps={
                        isFmdBreakpoint
                            ? getCompactXAxis({
                                  visualizationsTheme,
                                  theme,
                                  width,
                                  dataLength: data.length,
                                  xTickFormatter: value => formatOrdinal(+ranksMap[value])
                              })
                            : {}
                    }
                    yAxisProps={
                        isFmdBreakpoint
                            ? getCompactYAxis({
                                  visualizationsTheme,
                                  yTickFormatter: compactNumberFormatter(locale)
                              })
                            : {}
                    }
                    {...props}
                />
            </ChartContainer>
            <Legends
                keysMap={keysMap}
                limit={limit}
                hoveredKey={hoveredKey}
                hiddenKeys={hiddenKeys}
                dataKeys={dataKeys}
                emptyDataKeys={emptyDataKeys}
                selectedGames={selectedGames}
                onClick={onLegendClick}
                onMouseEnter={onLegendMouseEnter}
                onMouseLeave={onLegendMouseLeave}
            />
        </>
    );
};

export default LifetimePlaysBarChart;
